import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Clock, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_spray-wash-preview/artifacts/ycpj6xhu_logo.jpg%20%281%29.png';
const EVERWASH_URL = 'https://app.everwash.com/#membership-plans/143';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, toggle, t } = useLanguage();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/memberships', label: t('nav.memberships') },
    { path: '/services', label: t('nav.services') },
    { path: '/resources', label: t('nav.resources') },
    { path: '/contact', label: t('nav.contact') },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      {/* Top info bar */}
      <div className="bg-[#0F172A] text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-red-500" />
              <span>Open Daily: 9 AM - 7 PM</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-red-500" />
              <a href="tel:9182079463" className="hover:text-red-400 transition-colors">918-207-9463</a>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400">{t('nav.tagline')}</span>
            <button
              onClick={toggle}
              className="flex items-center gap-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full transition-colors"
              data-testid="lang-toggle-desktop"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === 'en' ? 'ES' : 'EN'}
            </button>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-xl shadow-lg' : 'bg-white'
        }`}
        data-testid="main-navigation"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="Super Spray Car Wash II" 
                className="h-12 md:h-14 w-auto"
              />
              <div className="hidden sm:block">
                <span className="text-[#1E3A8A] font-bold text-lg leading-tight block">Super Spray</span>
                <span className="text-slate-500 text-sm">Car Wash II</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link font-medium transition-colors ${
                    location.pathname === link.path 
                      ? 'text-[#DC2626]' 
                      : 'text-[#0F172A] hover:text-[#1E3A8A]'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href={EVERWASH_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-join-btn"
              >
                <Button 
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold px-6 py-2 rounded-md btn-primary"
                >
                  Join EverWash
                </Button>
              </a>
            </div>

            {/* Mobile: Lang toggle + Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={toggle}
                className="p-2 text-[#0F172A] flex items-center gap-1 text-xs font-bold bg-slate-100 rounded-full px-3"
                data-testid="lang-toggle-mobile"
              >
                <Globe className="w-3.5 h-3.5" />
                {lang === 'en' ? 'ES' : 'EN'}
              </button>
              <button
              className="p-2 text-[#0F172A]"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-4 space-y-2">
                {/* Mobile info */}
                <div className="pb-4 mb-4 border-b space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4 text-red-500" />
                    <span>{t('nav.open')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-red-500" />
                    <a href="tel:9182079463">918-207-9463</a>
                  </div>
                </div>

                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                      location.pathname === link.path 
                        ? 'bg-[#1E3A8A] text-white' 
                        : 'text-[#0F172A] hover:bg-slate-100'
                    }`}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}

                <a 
                  href={EVERWASH_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-4"
                  data-testid="mobile-join-btn"
                >
                  <Button 
                    className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold py-3 rounded-md"
                  >
                    Join EverWash
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navigation;
